import React, { useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from "recharts";
import { FiActivity, FiGlobe, FiAlertCircle, FiPackage, FiZap, FiRefreshCcw } from "react-icons/fi";
import { NewExcursionIcon, NewFliteIcon, NewHotelIcon } from "../../../helper/HwIcon";

const COLORS = {
    Flight: "#3B82F6",
    Hotel: "#10B981",
    Excursion: "#8B5CF6",
    Frontend: "#F59E0B",
    Payment: "#F59E0B",
    High: "#EF4444",
    Medium: "#F59E0B",
    Low: "#10B981",
};

const ErrorAnalysis = ({ data, refreshing = false, onRefresh = () => { }, loading = false }) => {
    const stats = useMemo(() => {
        const total = data.length;
        const flights = data.filter((e) => e.module === "Flight").length;
        const hotels = data.filter((e) => e.module === "Hotel").length;
        const excursions = data.filter((e) => e.module === "Excursion").length;
        const frontend = data.filter((e) => e.module === "Frontend").length;
        const critical = data.filter((e) => String(e.severity).toLowerCase() === "high").length;
        const moduleData = [
            { name: "Flights", value: flights, color: COLORS.Flight },
            { name: "Hotels", value: hotels, color: COLORS.Hotel },
            { name: "Excursions", value: excursions, color: COLORS.Excursion },
            { name: "Frontend", value: frontend, color: COLORS.Payment },
        ].filter((m) => m.value > 0);
        const vendorMap = data.reduce((acc, curr) => {
            const key = curr.vendor;
            if (!acc[key]) {
                acc[key] = {
                    name: curr.vendor,
                    count: 0,
                    module: curr.module
                };
            }
            acc[key].count += 1;
            return acc;
        }, {});
        const vendorData = Object.values(vendorMap);
        const trendMap = data.reduce((acc, curr) => {
            const iso = curr?.errorJson?.lastOccurredAt || curr?.errorJson?.firstOccurredAt || curr?.createdAt;
            const d = new Date(iso);
            const key = Number.isNaN(d.getTime()) ? String(curr.createdAt || "") : d.toISOString().slice(11, 16); // HH:MM
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
        const trendData = Object.keys(trendMap)
            .sort()
            .map((time) => ({
                time,
                errors: trendMap[time],
            }));
        const comparisonData = [
            { name: "Flights", count: flights },
            { name: "Hotels", count: hotels },
            { name: "Excursions", count: excursions },
            { name: "Frontend", count: frontend },
        ];

        return { total, flights, hotels, excursions, frontend, critical, moduleData, vendorData, trendData, comparisonData };
    }, [data]);

    return (
        <div className="em-analysis">
            <div className="em-section-header">
                <h2 className="h4">Error Analysis</h2>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <div className="em-badge em-badge--status">Live Analytics</div>
                    <button
                        type="button"
                        className="em-badge"
                        onClick={onRefresh}
                        disabled={refreshing || loading}
                    >
                        <FiRefreshCcw style={{ transform: "rotate(90deg)" }} />
                        {refreshing ? "Refreshing..." : "Refresh"}
                    </button>
                    {(refreshing || loading) && <span className="em-spinner" />}
                </div>
            </div>

            {stats.total === 0 && !loading ? (
                <div className="em-panel">
                    <p className="em-muted" style={{ margin: 0 }}>
                        No error data available. Once new errors are logged, charts and metrics will appear here.
                    </p>
                </div>
            ) : (
                <>

                    <div className="em-analysis-grid">
                        <div className="em-ana-card">
                            <div className="em-ana-card__icon"><FiActivity /></div>
                            <div className="em-ana-card__info">
                                <span className="em-ana-card__label">Total Errors</span>
                                <span className="em-ana-card__value">{stats.total}</span>
                            </div>
                        </div>
                        <div className="em-ana-card">
                            <div className="em-ana-card__icon" style={{ color: COLORS.Flight }}><NewFliteIcon /></div>
                            <div className="em-ana-card__info">
                                <span className="em-ana-card__label">Flight Errors</span>
                                <span className="em-ana-card__value">{stats.flights}</span>
                            </div>
                        </div>
                        <div className="em-ana-card">
                            <div className="em-ana-card__icon" style={{ color: COLORS.Hotel }}><NewHotelIcon /></div>
                            <div className="em-ana-card__info">
                                <span className="em-ana-card__label">Hotel Errors</span>
                                <span className="em-ana-card__value">{stats.hotels}</span>
                            </div>
                        </div>
                        {/* <div className="em-ana-card">
                    <div className="em-ana-card__icon" style={{ color: COLORS.Excursion }}><NewExcursionIcon /></div>
                    <div className="em-ana-card__info">
                        <span className="em-ana-card__label">Excursion Errors</span>
                        <span className="em-ana-card__value">{stats.excursions}</span>
                    </div>
                </div> */}
                        <div className="em-ana-card">
                            <div className="em-ana-card__icon" style={{ color: COLORS.Excursion }}><NewExcursionIcon /></div>
                            <div className="em-ana-card__info">
                                <span className="em-ana-card__label">Frontend Errors</span>
                                <span className="em-ana-card__value">{stats.frontend}</span>
                            </div>
                        </div>
                        <div className="em-ana-card em-ana-card--critical">
                            <div className="em-ana-card__icon"><FiAlertCircle /></div>
                            <div className="em-ana-card__info">
                                <span className="em-ana-card__label">Critical Errors</span>
                                <span className="em-ana-card__value">{stats.critical}</span>
                            </div>
                        </div>
                    </div>

                    <div className="em-charts-row">
                        <div className="em-chart-box">
                            <h3 className="em-chart-title">Module Distribution</h3>
                            <div className="em-chart-container">
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={stats.moduleData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {stats.moduleData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="em-chart-box">
                            <h3 className="em-chart-title">Errors per Vendor</h3>
                            <div className="em-chart-container">
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={stats.vendorData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />

                                        <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={12} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={12} />

                                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.02)' }} />

                                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                            {stats.vendorData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[entry.module] || COLORS.Flight}
                                                />
                                            ))}
                                        </Bar>

                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ErrorAnalysis;
